import type { IVector2 } from '@seddi/math-v2';
import type { Scene as SceneDesc } from '@seddi/scene';
import { Frustum, Matrix4, Object3D, Raycaster, Scene, Vector3 } from '@seddi/three';
import { Pointer, CURSORS } from '@seddi/ui-viewport-core';
import { isEqual } from 'lodash';
import { isHotkeyPressed } from 'react-hotkeys-hook';

import { ThreeBox } from '../../../renderer3/meshes/threeBox';
import { CameraTool, type CameraToolArgs, type CameraToolUpdateArgs } from './cameraTool';

export interface SelectionToolArgs extends CameraToolArgs {
  actions: Record<string, any>;
}

export interface SelectionToolUpdateArgs extends CameraToolUpdateArgs {
  hover: string[];
  selection: string[];
  blockedActions: Map<string, boolean>;
}

interface ISelecting {
  started?: boolean;
  box?: ThreeBox;
  origin?: IVector2;
}

export class SelectionTool extends CameraTool {
  actions: Record<string, any>;
  hover: string[];
  entities: Scene;
  raycaster: Raycaster;
  selectables: Object3D[];
  scene: SceneDesc;
  selecting: ISelecting;
  selection: string[];
  selectionObjects: Object3D[];
  _frustum: Frustum;
  _matrix: Matrix4;
  _vector: Vector3;
  blockedActions: Map<string, boolean>;
  isBlocked: boolean;

  /**
   * Creates a new 3D selection tool
   * @param args Constructor arguments that contains information about camera, camera controls, pointer, viewport and actions
   */
  constructor(args: SelectionToolArgs) {
    super(args);

    this.actions = args.actions;

    this.entities = new Scene();
    this.raycaster = new Raycaster();
    this.raycaster.params.Line.threshold = 0.005;

    this._frustum = new Frustum();
    this._matrix = new Matrix4();
    this._vector = new Vector3();

    this.blockedActions = new Map();
    this.isBlocked = false;
  }

  /**
   * Updates tool in terms of hover information, scene, selectables and selection objects with respect to the given arguments
   * @param args Update arguments that contains information about hover, renderables, scene and selection
   */
  onUpdate({ hover, renderables, scene, selection, blockedActions }: SelectionToolUpdateArgs): void {
    this.hover = hover;

    this.blockedActions = blockedActions;

    this.isBlocked =
      Object.keys(this.blockedActions).length > 0 ? this.blockedActions['VIEWPORT_3D_TOOL_SELECT'] : false;

    if (
      this.scene !== scene ||
      this.selection !== selection ||
      !isEqual(this.selectables, Array.from(renderables.values()))
    ) {
      this.scene = scene;
      this.selection = selection;

      this.selectables = Array.from(renderables.values());

      // Filter guard for corrupted processes.
      this.selectables = this.selectables.filter(
        (renderable: any) => renderable.geometry.attributes.position,
      );

      this.selectionObjects = this.selectables.filter((selectable) =>
        selection.includes(selectable.parent?.name),
      );
    }
  }

  /**
   * Updates the cursor and conducts pointer actions with respect to the given arguments
   * @param args Object contains distance, pointer and cursor information
   * @returns Flag indicating the camera needs update
   */
  onAnimationTick({ delta, cursor, pointer }: CameraToolUpdateArgs): boolean {
    // Process input
    if ((pointer.hover && !this.selecting) || this.isNavigating(pointer)) {
      this.processInput({ pointer });
    }

    if (
      this.selecting &&
      !isHotkeyPressed('alt') &&
      !isHotkeyPressed('space') &&
      !pointer.tertiaryPressed &&
      !this.isBlocked
    ) {
      // Process area selection
      const { selecting } = this;
      if (pointer.primaryDragDown) {
        this.onSelectionStart();
      }
      if (selecting.started) {
        this.onSelectionDrag({ pointer });
      }
      if (pointer.primaryUp || isHotkeyPressed('escape')) {
        this.onSelectionEnd({
          abort: isHotkeyPressed('escape'),
          multiple: isHotkeyPressed('shift'),
        });
        return true;
      }
      return selecting.started;
    }

    let needsUpdate = false;

    if (this.isCameraToolUpdating(pointer)) {
      this.removeSelectingCondition();
      needsUpdate = super.onAnimationTick({ delta, pointer });
    } else {
      pointer.setCursor(
        cursor || { image: isHotkeyPressed('space') ? CURSORS.GRAB : CURSORS.VIEWPORT_DEFAULT },
      );
    }
    return needsUpdate;
  }

  /**
   * Deletes the selecting information
   */
  removeSelectingCondition(): void {
    if (this.selecting) {
      delete this.selecting;
    }
  }

  /**
   * Checks if the pointer and keyboard inputs enable the camera tool to update
   * @param pointer Pointer input data
   * @returns Flag indicating the camera tool is updating
   */
  isCameraToolUpdating(pointer: Pointer): boolean {
    return (
      (pointer.primaryDrag && isHotkeyPressed('space')) ||
      pointer.secondaryDrag ||
      (pointer.primaryDown && isHotkeyPressed('space')) ||
      pointer.secondaryDown ||
      (pointer.hover && pointer.wheelEvent && !!(pointer.wheelEvent.deltaY || pointer.wheelEvent.deltaX)) ||
      pointer.locked ||
      isHotkeyPressed('space') ||
      isHotkeyPressed('alt') ||
      pointer.tertiaryPressed
    );
  }

  /**
   * Appends the current selecting box to the tool entities
   */
  onSelectionStart(): void {
    this.selecting.started = true;
    this.selecting.box = new ThreeBox();
    this.entities.add(this.selecting.box);
  }

  /**
   * Updates the selecting box with respect to the normalized pointer position information
   * @param args Object contains pointer information
   */
  onSelectionDrag({ pointer }: { pointer: Pointer }): void {
    this.selecting.box.update({
      camera: this.camera,
      points: [
        this.selecting.origin,
        {
          x: pointer.normalized.x,
          y: pointer.normalized.y,
        },
      ],
    });
  }

  /**
   * Updates the selection information with respect to given abort and multiple flags and to tool status
   * @param args Object contains flags for abort and multiple selection status
   */
  onSelectionEnd({ abort, multiple }: { abort: boolean; multiple: boolean }): void {
    const {
      actions,
      camera,
      entities,
      selectables,
      selectionObjects,
      selecting,
      _frustum,
      _matrix,
      _vector,
    } = this;

    delete this.selecting;

    if (selecting.started) {
      entities.remove(selecting.box);

      if (actions.select && !abort) {
        const { bounds } = selecting.box;
        const { aspect, far, fov, near } = camera;
        const height = near * Math.tan((Math.PI / 180) * 0.5 * fov);
        const width = aspect * height;
        _matrix.makePerspective(
          bounds.min.x * width,
          bounds.max.x * width,
          bounds.min.y * height,
          bounds.max.y * height,
          near,
          far,
        );
        _matrix.multiplyMatrices(_matrix, camera.matrixWorldInverse);
        _frustum.setFromProjectionMatrix(_matrix);

        let type = 'garmentPiece';
        if (selectionObjects.length) {
          const first = selectionObjects[0].parent;
          if (first?.userData?.avatar) {
            type = 'avatar';
          } else if (first.userData.garmentHem) {
            type = 'garmentHem';
          } else if (first.userData.garmentSeam) {
            type = 'garmentSeam';
          } else if (first.userData.light) {
            type = 'light';
          }
        }

        const selectablesInFrustum = selectables.filter((target: any) => {
          if (!!target.parent.userData[type] && _frustum.intersectsObject(target)) {
            const position = target.geometry.getAttribute('position');
            for (let i = 0; i < position.count; i += 1) {
              _vector
                .set(position.getX(i), position.getY(i), position.getZ(i))
                .applyMatrix4(target.matrixWorld);
              if (_frustum.containsPoint(_vector)) {
                return true;
              }
            }
          }
          return false;
        });

        selectablesInFrustum.forEach(({ parent: { name } }, i) =>
          actions.select({ id: name, multiple: multiple || i > 0 }),
        );

        if (!multiple && !selectablesInFrustum.length) {
          actions.select({});
        }
      }
    } else if (actions.select) {
      actions.select({});
    }
  }

  /**
   * Conducts hover, select and context menu actions with respect to the given user inputs
   * @param args Object contains pointer input data
   */
  processInput({ pointer }: { pointer: Pointer }): void {
    const { actions, camera, hover, raycaster, selectables, selectionObjects } = this;

    // Ray cast
    raycaster.setFromCamera(pointer.normalized, camera);
    const hit_ = raycaster.intersectObjects(selectables)[0];
    let hit: Object3D;
    if (hit_) {
      hit = hit_.object.parent;
    }

    // Hover
    if (this.isNavigating(pointer)) {
      hover.length && actions.hover([]);
    } else if ((hit && hover.indexOf(hit.name) === -1) || (!hit && hover.length)) {
      actions.hover(hit ? [hit.name] : []);
    }

    if (pointer.primaryDown && !isHotkeyPressed('space')) {
      if (!hit) {
        // Start area selection
        this.selecting = {
          origin: {
            x: pointer.normalized.x,
            y: pointer.normalized.y,
          },
        };
      } else if (actions.select && !this.isNavigating(pointer)) {
        // Select
        let multiple = isHotkeyPressed('shift');
        if (
          hit &&
          selectionObjects.length &&
          ((hit.userData.avatar && !selectionObjects[0].parent.userData.avatar) ||
            (hit.userData.garmentPiece && !selectionObjects[0].parent.userData.garmentPiece) ||
            (hit.userData.garmentHem && !selectionObjects[0].parent.userData.garmentHem) ||
            (hit.userData.garmentSeam && !selectionObjects[0].parent.userData.garmentSeam) ||
            (hit.userData.light && !selectionObjects[0].parent.userData.light))
        ) {
          multiple = false;
        }
        actions.select({ id: hit ? hit.name : false, multiple });
      }
    }
    this.checkContextualMenu({ pointer });
  }

  /**
   * Refreshes object intersections from camera with respect to the given pointer information
   * @param args Object contains pointer input data
   */
  checkContextualMenu({ pointer }: { pointer: Pointer }): void {
    const { camera, raycaster, selectables } = this;

    // Cast
    raycaster.setFromCamera(pointer.normalized, camera);
    _intersections.length = 0;

    raycaster.intersectObjects(selectables, false, _intersections);
    let hit = _intersections[0];
    if (hit) {
      hit = hit.object.parent;
    }
  }
}

const _intersections = [];
